var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
};
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { deepRecreate } from 'object-deep-recreate';
import { ActionsProvider, MutationsProvider, GlobalStoreProvider, GlobalGettersProvider } from './storeContext';
import { getStoreKeyModuleValues, getStoreModule, getStoreModuleName, getStoreStateWithModules, handleStateFillWithLocalValues, } from './helpers';
import { calcAndSetGettersValues, getGettersInitialValues } from './getters';
import { createStore } from './externalStore';
var withStore = function (Component, store, options) {
    if (options === void 0) { options = {}; }
    return function (props) {
        var initialValues = useRef(getGettersInitialValues(store));
        var globalStoreRef = useRef(createStore(store.state));
        var globalGettersRef = useRef(createStore(initialValues.current));
        var prevGettersRef = useRef(JSON.parse(JSON.stringify(initialValues.current)));
        var handleGettersValuesSet = useCallback(function (newValues) {
            calcAndSetGettersValues(store, newValues, globalGettersRef.current, prevGettersRef);
        }, [options.localStorageName]);
        useEffect(function () {
            var stateInitialValues = getStoreStateWithModules(store);
            if (options.localStorageName && stateInitialValues) {
                handleStateFillWithLocalValues(stateInitialValues, options.localStorageName);
            }
            globalStoreRef.current.setState(function () { return stateInitialValues; });
        }, []);
        var mutations = useMemo(function () {
            return getMutations(store, globalStoreRef.current, handleGettersValuesSet, options.localStorageName);
        }, [handleGettersValuesSet]);
        var actions = useMemo(function () {
            var actionsFns = getStoreKeyModuleValues(store, 'actions');
            return actionsFns !== null && actionsFns !== void 0 ? actionsFns : {};
        }, []);
        var MemoizedComponent = useMemo(function () { return memo(Component); }, []);
        return (React.createElement(MutationsProvider, { value: mutations },
            React.createElement(ActionsProvider, { value: actions },
                React.createElement(GlobalStoreProvider, { value: globalStoreRef.current },
                    React.createElement(GlobalGettersProvider, { value: globalGettersRef },
                        React.createElement(MemoizedComponent, __assign({}, props)))))));
    };
};
var getMutations = function (storeConfig, globalStore, handleGettersValuesSet, storageName) {
    var mutations = getStoreKeyModuleValues(storeConfig, 'mutations');
    var mutationNames = Object.keys(mutations);
    if (!mutationNames.length)
        return {};
    var values = {};
    mutationNames.forEach(function (mutationName) {
        var originalFn = mutations[mutationName];
        values[mutationName] = function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            if (!globalStore) {
                throw new Error('No store found');
            }
            var setter = function (state) {
                var prevStateCloned = JSON.parse(JSON.stringify(state));
                var moduleNames = mutationName.split('/');
                // alter the state with the logic given in the store config
                if (moduleNames.length === 1) {
                    originalFn.apply(void 0, __spreadArray([state], args));
                }
                else {
                    var moduleName = getStoreModuleName(mutationName);
                    var moduleState = getStoreModule(state, moduleName);
                    originalFn.apply(void 0, __spreadArray([moduleState], args));
                }
                var newValues = deepRecreate(state, prevStateCloned);
                handleGettersValuesSet(newValues);
                if (storageName) {
                    localStorage.setItem(storageName, JSON.stringify(newValues));
                }
                return newValues;
            };
            globalStore.setState(setter);
        };
    });
    return values;
};
export default withStore;
